import { css, unsafeCSS } from "lit";
import { catppuccin } from "@newsfeed/shared";

const { latte, mocha } = catppuccin;

/**
 * Foundation stylesheet shared by every component via a single constructable
 * stylesheet (Lit deduplicates and adopts it across shadow roots).
 *
 * Layers, top to bottom:
 *   1. modern reset
 *   2. Catppuccin primitives (--ctp-*), light + dark
 *   3. semantic + scale tokens (--nf-*) that leaves consume
 * Component styles reference only the --nf-* layer.
 */
export const sharedStyles = css`
  /* 1. Modern reset */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  * {
    margin: 0;
  }
  img,
  picture,
  video,
  canvas,
  svg {
    display: block;
    max-width: 100%;
  }
  input,
  button,
  textarea,
  select {
    font: inherit;
  }
  p,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    overflow-wrap: break-word;
  }

  :host {
    /* 2. Catppuccin primitives — light */
    --ctp-base: ${unsafeCSS(latte.base)};
    --ctp-mantle: ${unsafeCSS(latte.mantle)};
    --ctp-surface0: ${unsafeCSS(latte.surface0)};
    --ctp-surface1: ${unsafeCSS(latte.surface1)};
    --ctp-text: ${unsafeCSS(latte.text)};
    --ctp-subtext0: ${unsafeCSS(latte.subtext0)};
    --ctp-red: ${unsafeCSS(latte.red)};
    --ctp-green: ${unsafeCSS(latte.green)};
    --ctp-peach: ${unsafeCSS(latte.peach)};
    --ctp-mauve: ${unsafeCSS(latte.mauve)};

    /* 3. Semantic tokens */
    --nf-bg: var(--ctp-base);
    --nf-surface: var(--ctp-mantle);
    --nf-surface-2: var(--ctp-surface0);
    --nf-border: var(--ctp-surface1);
    --nf-text: var(--ctp-text);
    --nf-muted: var(--ctp-subtext0);
    --nf-danger: var(--ctp-red);
    --nf-success: var(--ctp-green);
    --nf-warning: var(--ctp-peach);
    --nf-accent: var(--ctp-mauve);
    --nf-accent-fg: var(--ctp-base);

    /* Scale tokens (spacing + type) */
    --nf-radius: 8px;
    --nf-space-1: 1px;
    --nf-space-2: 2px;
    --nf-space-4: 4px;
    --nf-space-6: 6px;
    --nf-space-8: 8px;
    --nf-space-10: 10px;
    --nf-space-12: 12px;
    --nf-space-16: 16px;
    --nf-space-24: 24px;
    --nf-font-11: 11px;
    --nf-font-13: 13px;
    --nf-font-14: 14px;
    --nf-font-15: 15px;
    --nf-font-18: 18px;

    color: var(--nf-text);
    font-family:
      system-ui,
      -apple-system,
      "Segoe UI",
      Roboto,
      sans-serif;
    font-size: var(--nf-font-15);
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    box-sizing: border-box;
  }

  @media (prefers-color-scheme: dark) {
    :host {
      /* 2. Catppuccin primitives — dark */
      --ctp-base: ${unsafeCSS(mocha.base)};
      --ctp-mantle: ${unsafeCSS(mocha.mantle)};
      --ctp-surface0: ${unsafeCSS(mocha.surface0)};
      --ctp-surface1: ${unsafeCSS(mocha.surface1)};
      --ctp-text: ${unsafeCSS(mocha.text)};
      --ctp-subtext0: ${unsafeCSS(mocha.subtext0)};
      --ctp-red: ${unsafeCSS(mocha.red)};
      --ctp-green: ${unsafeCSS(mocha.green)};
      --ctp-peach: ${unsafeCSS(mocha.peach)};
      --ctp-mauve: ${unsafeCSS(mocha.mauve)};
    }
  }

  button {
    color: var(--nf-text);
    background: var(--nf-surface-2);
    border: 1px solid var(--nf-border);
    border-radius: var(--nf-radius);
    padding: var(--nf-space-6) var(--nf-space-10);
    cursor: pointer;
  }
  button:hover {
    background: var(--nf-border);
  }
  button.primary {
    background: var(--nf-accent);
    color: var(--nf-accent-fg);
    border-color: var(--nf-accent);
  }
  button.danger {
    color: var(--nf-danger);
    border-color: var(--nf-danger);
  }
  button:disabled {
    opacity: 0.5;
    cursor: default;
  }

  input,
  select,
  textarea {
    color: var(--nf-text);
    background: var(--nf-bg);
    border: 1px solid var(--nf-border);
    border-radius: var(--nf-radius);
    padding: var(--nf-space-6) var(--nf-space-8);
  }

  a {
    color: var(--nf-accent);
  }

  h1 {
    font-family: "Kaushan Script", cursive;
  }

  .muted {
    color: var(--nf-muted);
  }
  .row {
    display: flex;
    gap: var(--nf-space-12);
    align-items: center;
  }
  .col {
    display: flex;
    flex-direction: column;
    gap: var(--nf-space-12);
  }
  .card {
    background: var(--nf-surface);
    border: 1px solid var(--nf-border);
    border-radius: var(--nf-radius);
    padding: var(--nf-space-12);
  }
  .empty {
    color: var(--nf-muted);
    padding: var(--nf-space-24);
    text-align: center;
  }
  .error {
    color: var(--nf-danger);
  }
  .badge {
    display: inline-block;
    font-size: var(--nf-font-11);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: var(--nf-space-1) var(--nf-space-6);
    border-radius: 999px;
    background: var(--nf-accent);
    color: var(--nf-accent-fg);
  }
  .spinner {
    display: inline-block;
    width: 1em;
    height: 1em;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    vertical-align: -0.1em;
    animation: nf-spin 0.7s linear infinite;
  }
  @keyframes nf-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
